import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CustomDropdown from '@components/CustomDropdown';
import CustomReactSelect from '@components/CustomReactSelect';
import ModalContainer from '@components/modals/ModalContainer';
import ModalHeader from '@components/modals/ModalHeader';
import { toastSuccess } from '@components/Toast';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { getCategories, getFabricTypes, getMarkets } from '@store/design/action';
import { getAllCategories, getAllFabricTypes, getAllMarkets } from '@store/reducers';

import { addRangePlanItem } from '@services/rangePlanService';

import { printApiError } from '@utils/Util';

import { ADD_ITEMS_INITIAL_VALUE } from '@/components/rangePlan/constants';

const AddItemModal = ({ onClose, id, getRangePlanTableData, entityType, brandId = null }) => {
	const allMarkets = useSelector(getAllMarkets);
	const allCategories = useSelector(getAllCategories);
	const allFabricTypes = useSelector(getAllFabricTypes);
	const [markets, setMarkets] = useState([]);
	const [categories, setCategories] = useState([]);
	const [selectedValues, setSelectedValues] = useState(ADD_ITEMS_INITIAL_VALUE);
	const dispatch = useDispatch();
	const [errors, setErrors] = useState(null);

	useEffect(() => {
		dispatch(getMarkets());
		dispatch(getFabricTypes());
		dispatch(getCategories());
	}, []);

	const updateItemsWithValue = (items, key, labelKey) => {
		return items.map((item) => ({
			...item,
			value: item[key],
			label: item[labelKey]
		}));
	};

	useEffect(() => {
		setMarkets(updateItemsWithValue(allMarkets, 'name', 'name'));
		setCategories(updateItemsWithValue(allCategories, 'id', 'name'));
	}, [allMarkets, allFabricTypes, allCategories]);

	const resetForm = () => {
		setSelectedValues(ADD_ITEMS_INITIAL_VALUE);
	};

	const validateForm = () => {
		const errors = {};
		if (!selectedValues?.market?.id) {
			errors.market = 'Market is required';
		}
		if (!selectedValues?.fabricType?.code) {
			errors.fabricType = 'Fabric Type is required';
		}
		if (!selectedValues?.category?.length) {
			errors.category = 'Category is required';
		}
		setErrors(errors);
		return errors;
	};

	const handleSubmit = async () => {
		if (Object.keys(validateForm()).length !== 0) {
			return;
		}
		try {
			dispatch(openLoader());

			const requestBody = selectedValues?.category?.map((category) => ({
				categoryId: category?.id,
				fabricType: selectedValues?.fabricType?.code,
				productGroupId: selectedValues?.market?.id,
				entityId: id,
				entityType: entityType
			}));

			const data = await addRangePlanItem(requestBody, brandId);
			if (data?.success) {
				toastSuccess(data?.message);
				onClose();
				resetForm();
				getRangePlanTableData();
			}
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	return (
		<ModalContainer className="range-add-item-modal">
			<div className="common-popup range-add-item-modal">
				<ModalHeader title="Add Item*" onClose={onClose} />
				<div className="common-popup-body">
					<div className="row">
						<div className="col-md-6 pr-2">
							<div className="form-group">
								<div className="select-component">
									<label htmlFor="collection">Market</label>
									<CustomDropdown
										title="Select"
										items={markets}
										onItemClick={(value) =>
											setSelectedValues((prev) => ({ ...prev, market: value }))
										}
										selectedItem={selectedValues?.market?.name}
									/>
									{errors?.market && <p className="error">{errors?.market}</p>}
								</div>
							</div>
						</div>

						<div className="col-md-6 pr-2">
							<div className="form-group">
								<div className="select-component">
									<label htmlFor="collection">Fabric Type</label>
									<CustomDropdown
										title="Select"
										items={allFabricTypes}
										onItemClick={(value) =>
											setSelectedValues((prev) => ({ ...prev, fabricType: value }))
										}
										selectedItem={selectedValues?.fabricType?.value}
									/>
									{errors?.fabricType && <p className="error">{errors?.fabricType}</p>}
								</div>
							</div>
						</div>

						<div className="col-md-12">
							<div className="select-component chip-component mb-3 show">
								<label htmlFor="collection">Category</label>
								<CustomReactSelect
									allItems={categories}
									onSetSelectedItem={(value) =>
										setSelectedValues((prev) => ({ ...prev, category: value }))
									}
									selectedValue={selectedValues?.category}
									title={'Select'}
									isMulti={true}
								/>
								{errors?.category && <p className="error">{errors?.category}</p>}
							</div>
						</div>
					</div>
				</div>
				<div className="modal-footer">
					<div className="footer-buttons text-right">
						<button className="button gray-outline size32" onClick={onClose}>
							Cancel
						</button>
						<button className="button size32 ml-2" onClick={handleSubmit}>
							Submit
						</button>
					</div>
				</div>
			</div>
		</ModalContainer>
	);
};

export default AddItemModal;
